export type MutationMethod = 'POST' | 'PUT' | 'DELETE';

export type MutationIdempotencyClassification =
  | 'retry-safe-with-idempotency-key'
  | 'non-retryable';

export interface MutationIdempotencyRule {
  method: MutationMethod;
  pathTemplate: string;
  classification: MutationIdempotencyClassification;
  reason: string;
}

export interface AttachmentIdempotencyKeyInput {
  entityType: string;
  entityId: string;
  fileType: string;
  fileName: string;
  fileSize: number;
  fileLastModified: number;
}

const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;
const ROUTE_TEMPLATE_PARAM_REGEX = /:[^/]+/g;

export const MUTATION_IDEMPOTENCY_RULES: readonly MutationIdempotencyRule[] = [
  { method: 'POST', pathTemplate: '/api/attachments', classification: 'non-retryable', reason: 'Attachment uploads are side-effecting and can duplicate objects in storage.' },
  { method: 'DELETE', pathTemplate: '/api/attachments/:id', classification: 'non-retryable', reason: 'Attachment deletes are destructive and should not auto-retry after auth refresh.' },

  { method: 'POST', pathTemplate: '/api/leases', classification: 'non-retryable', reason: 'Lease creation mutates multiple dependent records.' },
  { method: 'PUT', pathTemplate: '/api/leases/:leaseId', classification: 'non-retryable', reason: 'Lease updates can alter contractual terms and should be explicit.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/terminate', classification: 'non-retryable', reason: 'Termination is high-impact and intentionally single-shot.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/rent-schedules', classification: 'non-retryable', reason: 'Rent schedule creation can produce duplicate financial windows.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/guarantors/:guarantorId', classification: 'non-retryable', reason: 'Guarantor linkage should not auto-replay.' },
  { method: 'DELETE', pathTemplate: '/api/leases/:leaseId/guarantors/:guarantorId', classification: 'non-retryable', reason: 'Guarantor unlinking should stay explicit.' },

  { method: 'POST', pathTemplate: '/api/insurance-policies', classification: 'non-retryable', reason: 'Policy creation can duplicate legal records.' },
  { method: 'PUT', pathTemplate: '/api/insurance-policies/:id', classification: 'non-retryable', reason: 'Policy updates should not auto-retry on auth refresh.' },
  { method: 'DELETE', pathTemplate: '/api/insurance-policies/:id', classification: 'non-retryable', reason: 'Policy deletion is destructive.' },

  { method: 'POST', pathTemplate: '/api/units/:unitId/inspections', classification: 'non-retryable', reason: 'Inspection creation can duplicate compliance events.' },
  { method: 'POST', pathTemplate: '/api/inspections', classification: 'non-retryable', reason: 'Inspection creation can duplicate compliance events.' },
  { method: 'PUT', pathTemplate: '/api/inspections/:id', classification: 'non-retryable', reason: 'Inspection updates should be explicit.' },
  { method: 'DELETE', pathTemplate: '/api/inspections/:id', classification: 'non-retryable', reason: 'Inspection deletion is destructive.' },

  { method: 'POST', pathTemplate: '/api/properties', classification: 'non-retryable', reason: 'Property creation can duplicate inventory records.' },
  { method: 'PUT', pathTemplate: '/api/properties/:id', classification: 'non-retryable', reason: 'Property updates should not auto-retry.' },
  { method: 'DELETE', pathTemplate: '/api/properties/:id', classification: 'non-retryable', reason: 'Property deletion is destructive.' },

  { method: 'POST', pathTemplate: '/api/units', classification: 'non-retryable', reason: 'Unit creation can duplicate rentable inventory.' },
  { method: 'PUT', pathTemplate: '/api/units/:id', classification: 'non-retryable', reason: 'Unit updates should be explicit.' },
  { method: 'DELETE', pathTemplate: '/api/units/:id', classification: 'non-retryable', reason: 'Unit deletion is destructive.' },

  { method: 'POST', pathTemplate: '/api/guarantors', classification: 'non-retryable', reason: 'Guarantor creation can duplicate legal parties.' },
  { method: 'PUT', pathTemplate: '/api/guarantors/:id', classification: 'non-retryable', reason: 'Guarantor updates should not auto-retry.' },

  { method: 'POST', pathTemplate: '/api/tenants', classification: 'non-retryable', reason: 'Tenant creation can duplicate legal entities.' },
  { method: 'PUT', pathTemplate: '/api/tenants/:id', classification: 'non-retryable', reason: 'Tenant updates should be explicit.' },
  { method: 'DELETE', pathTemplate: '/api/tenants/:id', classification: 'non-retryable', reason: 'Tenant deletion is destructive.' },

  { method: 'POST', pathTemplate: '/api/leases/:leaseId/charges', classification: 'non-retryable', reason: 'Charge creation can double-bill if replayed blindly.' },
  { method: 'PUT', pathTemplate: '/api/charges/:id', classification: 'non-retryable', reason: 'Charge updates should not auto-retry.' },
  { method: 'POST', pathTemplate: '/api/charges/:id/void', classification: 'non-retryable', reason: 'Charge voiding is a high-impact mutation.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/charges/generate-rent', classification: 'non-retryable', reason: 'Batch charge generation is operationally controlled and should not auto-retry on auth refresh.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/charges/generate-utilities', classification: 'non-retryable', reason: 'Batch utility generation is operationally controlled and should not auto-retry.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/charges/generate-late-fee', classification: 'non-retryable', reason: 'Late-fee generation should remain an explicit operator action.' },

  { method: 'POST', pathTemplate: '/api/tax-parcels', classification: 'non-retryable', reason: 'Tax parcel creation can duplicate assessor records.' },
  { method: 'PUT', pathTemplate: '/api/tax-parcels/:id', classification: 'non-retryable', reason: 'Tax parcel updates should be explicit.' },
  { method: 'DELETE', pathTemplate: '/api/tax-parcels/:id', classification: 'non-retryable', reason: 'Tax parcel deletion is destructive.' },
  { method: 'POST', pathTemplate: '/api/tax-parcels/:id/fetch-assessor', classification: 'non-retryable', reason: 'Assessor fetch/apply flows are operator-driven and should not auto-retry.' },
  { method: 'POST', pathTemplate: '/api/tax-parcels/:id/apply-assessor', classification: 'non-retryable', reason: 'Assessor apply mutates local data and should stay explicit.' },

  { method: 'POST', pathTemplate: '/api/leases/:leaseId/payments', classification: 'retry-safe-with-idempotency-key', reason: 'Payment creation enforces deterministic replay semantics when an Idempotency-Key is present.' },
  { method: 'POST', pathTemplate: '/api/payments/:id/void', classification: 'non-retryable', reason: 'Payment void is high-impact and should not auto-retry.' },

  { method: 'POST', pathTemplate: '/api/admin/generate-monthly-charges', classification: 'non-retryable', reason: 'Admin batch generation is intentionally operator-controlled.' },
  { method: 'POST', pathTemplate: '/api/leases/:leaseId/documents/lease-agreement', classification: 'non-retryable', reason: 'Document generation has side effects and should not auto-replay.' },
];

const MUTATION_METHODS = new Set<MutationMethod>(['POST', 'PUT', 'DELETE']);

function routeTemplateToRegex(pathTemplate: string): RegExp {
  const escaped = pathTemplate.replace(REGEX_ESCAPE_PATTERN, '\\$&');
  const withParams = escaped.replace(ROUTE_TEMPLATE_PARAM_REGEX, '[^/]+');
  return new RegExp(`^${withParams}$`);
}

function normalizePathname(pathOrUrl: string): string | null {
  try {
    return new URL(pathOrUrl, 'https://idempotency.local').pathname;
  } catch {
    return null;
  }
}

export function sanitizeIdempotencySegment(value: string, maxLength = 120): string {
  const sanitized = value
    .replace(/[\r\n:\u0000]/g, '_')
    .slice(0, maxLength);

  return sanitized.length > 0 ? sanitized : 'none';
}

function sanitizeSegment(value: string, maxLength: number): string {
  return sanitizeIdempotencySegment(value, maxLength);
}

function normalizePaymentDate(rawDate: string): string {
  const trimmed = rawDate.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const timestamp = Date.parse(trimmed);
  if (Number.isNaN(timestamp)) return trimmed;
  return new Date(timestamp).toISOString().slice(0, 10);
}

function toCents(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100);
}

const mutationRulesWithMatcher = MUTATION_IDEMPOTENCY_RULES.map((rule) => ({
  ...rule,
  matcher: routeTemplateToRegex(rule.pathTemplate),
}));

const mutationRulesByTemplate = new Map<string, MutationIdempotencyRule>(
  MUTATION_IDEMPOTENCY_RULES.map((rule) => [`${rule.method} ${rule.pathTemplate}`, rule]),
);

export interface PaymentIdempotencyKeyInput {
  leaseId: string;
  paymentDate: string;
  amount: number;
  memo?: string | null;
}

export function isMutationMethod(method: string): method is MutationMethod {
  return MUTATION_METHODS.has(method.toUpperCase() as MutationMethod);
}

export function getMutationIdempotencyPolicyForTemplate(
  method: string,
  pathTemplate: string,
): MutationIdempotencyRule | null {
  const normalizedMethod = method.toUpperCase();
  if (!isMutationMethod(normalizedMethod)) return null;
  return mutationRulesByTemplate.get(`${normalizedMethod} ${pathTemplate}`) ?? null;
}

export function getMutationIdempotencyPolicyForPath(
  method: string,
  pathOrUrl: string,
): MutationIdempotencyRule | null {
  const normalizedMethod = method.toUpperCase();
  if (!isMutationMethod(normalizedMethod)) return null;

  const pathname = normalizePathname(pathOrUrl);
  if (!pathname) return null;

  for (const rule of mutationRulesWithMatcher) {
    if (rule.method !== normalizedMethod) continue;
    if (rule.matcher.test(pathname)) {
      return rule;
    }
  }

  return null;
}

export function buildPaymentIdempotencyKey(input: PaymentIdempotencyKeyInput): string {
  if (!Number.isFinite(input.amount)) {
    throw new Error('Payment amount must be a finite number');
  }

  const leaseId = sanitizeSegment(input.leaseId, 80);
  const paymentDate = sanitizeSegment(normalizePaymentDate(input.paymentDate), 20);
  const amountCents = toCents(input.amount);
  const memo = sanitizeSegment(input.memo ?? '', 120);

  return `payment:${leaseId}:${paymentDate}:${amountCents}:${memo}`;
}

export function buildAttachmentIdempotencyKey(input: AttachmentIdempotencyKeyInput): string {
  if (!Number.isFinite(input.fileSize) || input.fileSize < 0) {
    throw new Error('Attachment fileSize must be a non-negative finite number');
  }
  if (!Number.isFinite(input.fileLastModified) || input.fileLastModified < 0) {
    throw new Error('Attachment fileLastModified must be a non-negative finite number');
  }

  const entityType = sanitizeSegment(input.entityType, 80);
  const entityId = sanitizeSegment(input.entityId, 80);
  const fileType = sanitizeSegment(input.fileType, 60);
  const fileName = sanitizeSegment(input.fileName, 120);
  const fileSize = Math.trunc(input.fileSize);
  const fileLastModified = Math.trunc(input.fileLastModified);

  return `attachment:${entityType}:${entityId}:${fileType}:${fileName}:${fileSize}:${fileLastModified}`;
}
